import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Bid, PartnerRequest, listMyWork } from '../../api/marketplace';
import {
  BID_STATUS_LABELS,
  formatDateTime,
  formatPrice,
  serviceLabel,
  statusLabel,
} from './marketplaceFormat';
import './Marketplace.css';

const PartnerMyWorkPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<{ request: PartnerRequest; bid: Bid }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      setItems(await listMyWork(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your work.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="marketplace-page">
      <div className="task-view-header">
        <h2>My work</h2>
        <button type="button" className="download-button" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>
      {loading && items.length === 0 ? (
        <p>Loading...</p>
      ) : error ? (
        <div className="status-error-banner" role="alert">{error}</div>
      ) : items.length === 0 ? (
        <p className="marketplace-empty">You have not bid on any requests yet.</p>
      ) : (
        <table className="marketplace-table">
          <thead>
            <tr>
              <th>Request</th>
              <th>Request status</th>
              <th>My bid</th>
              <th>Bid status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map(({ request, bid }) => (
              <tr key={request.id}>
                <td>
                  <Link to={`/marketplace/requests/${request.id}`} className="service-link">
                    {serviceLabel(request.serviceType)}
                  </Link>
                </td>
                <td>{statusLabel(request.status)}</td>
                <td>{formatPrice(bid.priceCents, bid.priceType)}, {bid.etaDays} days</td>
                <td>
                  <span className={`marketplace-badge marketplace-badge--${bid.status}`}>
                    {BID_STATUS_LABELS[bid.status] || bid.status}
                  </span>
                </td>
                <td>{formatDateTime(bid.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PartnerMyWorkPage;
